// The new organisation set-up wizard: five steps (Your company, What you make, Starter lists, Your team, Done) with a
// step bar, Back / Next / Finish later. It opens by itself for the organisation's main Admin on first sign-in while the
// set-up isn't finished; "Finish later" keeps what was entered and the wizard resumes at the same step. Any Admin can
// open it again from Settings → Organisation → Set-up wizard; re-running never deletes anything.
import { ApiError, esc, get, post, put, toast } from "../lib.js";
import { navigate, onAction, onChange, refreshSession, route, state } from "../state.js";
import { errorPanel, loadingPanel, renderShell, setContent } from "../shell.js";
import { prepareLogo, LOGO_ACCEPT } from "../logo.js";
import { ALL_CURRENCIES, COUNTRIES } from "../../../shared/countries.js";
import {
  GARMENT_SIZES, INDUSTRIES, SETUP_ROLES, SETUP_STEPS, SHOE_SIZES, STARTER_COLOURS, STARTER_STANDARDS, industryLabel, starterListsFor, starterSeasons, teamProblems,
  whatYouGet, type StarterListKey,
} from "../../../shared/setup.js";

interface Row { name: string; email: string; role: string }
interface Wiz {
  step: number;
  company: { name: string; countryCode: string; timezone: string; currency: string; tradingCurrency: string };
  industries: string[];
  lists: Record<StarterListKey, string[] | boolean>;
  team: Row[];
  summary: { company?: string; industries?: string[]; added?: Record<string, string[]>; invited?: Array<{ name: string; role: string; email: string; emailSent: boolean; inviteLink?: string }> };
}
let W: Wiz;
let S: any = null;                    // what the server said (seats, people already in Falcon, the year, logo)
let bad: Record<string, string> = {}; // boxes to show red: key -> message
let teamBad: Array<Record<string, string>> = [];
let busy = false;

const ZONES: string[] = (() => {
  try { return (Intl as any).supportedValuesOf("timeZone") as string[]; } catch { return [...new Set(COUNTRIES.map((c) => c.tz))].sort(); }
})();
const countryName = (code: string) => COUNTRIES.find((c) => c.code === code)?.name || "";
const ALL_LISTS = (year: number): Record<StarterListKey, string[] | boolean> => ({
  garmentSizes: [...GARMENT_SIZES], shoeSizes: [...SHOE_SIZES], standards: [...STARTER_STANDARDS], seasons: starterSeasons(year),
  colours: STARTER_COLOURS.map((c) => c.value), qc: true,
});

route("/app/setup", async () => {
  renderShell({ active: "settings", title: "Set up Falcon", subtitle: "A few quick steps to get your organisation ready.", content: loadingPanel() });
  try { S = await get("/api/org/setup"); } catch (err) { return setContent(errorPanel((err as Error).message)); }
  const p = S.progress?.data || {};
  W = {
    step: Math.min(5, Math.max(1, Number(S.progress?.step) || 1)),
    company: { ...S.company, ...(p.company || {}) },
    industries: Array.isArray(p.industries) ? p.industries : S.industries,
    lists: { ...ALL_LISTS(S.year), ...(p.lists || {}) },
    team: Array.isArray(p.team) && p.team.length ? p.team : [{ name: "", email: "", role: "merchandiser" }],
    summary: p.summary || {},
  };
  if (!W.company.tradingCurrency) W.company.tradingCurrency = "USD";
  bad = {}; teamBad = [];
  draw();
});

// ---- Drawing ---------------------------------------------------------------------------------------------------------
function stepBar(): string {
  return `<ol class="sw-steps" aria-label="Set-up steps">${SETUP_STEPS.map((label, i) => {
    const n = i + 1, cls = n === W.step ? "is-current" : n < W.step ? "is-done" : "";
    return `<li class="${cls}" ${n === W.step ? 'aria-current="step"' : ""}><span class="sw-dot">${n < W.step ? "✓" : n}</span><span class="sw-label">${esc(label)}</span></li>`;
  }).join("")}</ol>`;
}
const box = (key: string, label: string, inner: string, hint = "") =>
  `<div class="sw-box ${bad[key] ? "is-bad" : ""}" data-sw-box="${key}"><label class="sw-box-label">${label}</label>${inner}${hint ? `<small class="sw-hint">${hint}</small>` : ""}${bad[key] ? `<small class="sw-err" role="alert">${esc(bad[key])}</small>` : ""}</div>`;

function step1(): string {
  const c = W.company;
  const opt = (list: string[], cur: string) => list.map((x) => `<option ${x === cur ? "selected" : ""}>${esc(x)}</option>`).join("");
  return `<div class="sw-grid">
${box("name", "Company name *", `<input id="sw-name" data-sw="company.name" value="${esc(c.name)}" maxlength="120" autocomplete="organization">`)}
${box("country", "Country *", `<input id="sw-country" list="sw-countries" value="${esc(countryName(c.countryCode))}" placeholder="Start typing…" autocomplete="off"><datalist id="sw-countries">${COUNTRIES.map((x) => `<option value="${esc(x.name)}"></option>`).join("")}</datalist>`, "Fills in the time zone and local currency. You can change both.")}
${box("timezone", "Time zone", `<select id="sw-tz" data-sw="company.timezone">${ZONES.includes(c.timezone) ? "" : `<option selected>${esc(c.timezone)}</option>`}${opt(ZONES, c.timezone)}</select>`)}
${box("currency", "Local currency", `<select id="sw-cur" data-sw="company.currency">${opt(ALL_CURRENCIES, c.currency)}</select>`)}
${box("tradingCurrency", "Trading currency", `<select id="sw-trade" data-sw="company.tradingCurrency">${opt(ALL_CURRENCIES, c.tradingCurrency || "USD")}</select>`, "The currency you buy and sell in; Finance uses it.")}
${box("logo", "Logo (optional)", `<div class="sw-logo">${S.company.hasLogo || state.session.organisation?.logoUrl ? `<img src="${esc(state.session.organisation?.logoUrl || "/api/org/logo")}" alt="Your logo">` : '<span class="muted">No logo yet</span>'}<label class="secondary small-btn sw-file">Upload logo<input type="file" accept="${LOGO_ACCEPT}" hidden data-change="sw-logo"></label></div>`)}
</div>`;
}
function step2(): string {
  return `${box("industries", "What do you make? Pick at least one.", `<div class="sw-cards" role="group" aria-label="What you make">${INDUSTRIES.map(([k, label]) => {
    const on = W.industries.includes(k);
    return `<button type="button" class="sw-card ${on ? "is-on" : ""}" data-action="sw-industry" data-k="${k}" aria-pressed="${on}"><span class="sw-tick" aria-hidden="true">${on ? "✓" : ""}</span><span>${esc(label)}</span></button>`;
  }).join("")}</div>`)}
<div class="sw-get"><h4>What you get</h4><ul id="sw-get">${getList()}</ul><small class="sw-hint">Anything Off is only hidden from menus and forms; nothing is deleted. Change it any time in Settings → Organisation → What you make.</small></div>`;
}
const getList = () => whatYouGet(W.industries).map((g) => `<li class="${g.on ? "is-on" : ""}"><span class="sw-onoff">${g.always ? "Always on" : g.on ? "On" : "Off"}</span>${esc(g.label)}</li>`).join("");

function step3(): string {
  const show = starterListsFor(W.industries);
  const ticks = (key: StarterListKey, title: string, all: string[]) => {
    if (!show.includes(key)) return "";
    const chosen = (W.lists[key] as string[]) || [];
    return `<fieldset class="sw-list" data-list="${key}"><legend>${esc(title)}</legend><div class="sw-ticks">${all.map((v) => `<label class="check-row"><input type="checkbox" data-sw-tick="${key}" value="${esc(v)}" ${chosen.includes(v) ? "checked" : ""}><span>${esc(v)}</span></label>`).join("")}</div></fieldset>`;
  };
  const shoe = SHOE_SIZES;
  const qcWhat = [W.industries.some((k) => k !== "footwear") ? "garment" : "", W.industries.includes("footwear") ? "footwear" : ""].filter(Boolean).join(" and ");
  return `<p class="sw-intro muted">Everything is ticked. Untick what you don't want; only ticked items are added, and nothing already there is added twice.</p>
<div class="sw-lists">
${ticks("garmentSizes", "Garment sizes", GARMENT_SIZES)}
${ticks("shoeSizes", "Shoe sizes", shoe)}
${ticks("standards", "Standards (Workwear and PPE)", STARTER_STANDARDS)}
${ticks("seasons", "Seasons (Fashion)", starterSeasons(S.year))}
${ticks("colours", "Colours", STARTER_COLOURS.map((c) => c.value))}
<fieldset class="sw-list" data-list="qc"><legend>QC checklist</legend><label class="check-row"><input type="checkbox" data-sw-qc ${W.lists.qc ? "checked" : ""}><span>The organisation's QC checklist${qcWhat ? ` with ${esc(qcWhat)} sections` : ""} (Settings → QC)</span></label></fieldset>
</div>`;
}
function seatsLeft() { return Math.max(0, Number(S.seats.limit) - Number(S.seats.used)); }
function teamRows(): Row[] { return W.team.filter((r) => r.name.trim() || r.email.trim()); }
function step4(): string {
  const left = seatsLeft(), n = teamRows().length;
  return `<p class="sw-intro muted">Optional. Each person gets the usual invitation email and the permissions of their role; you can fine-tune them later in Users &amp; Permissions.</p>
<div class="sw-seats ${n > left ? "is-over" : ""}" id="sw-seats" role="status">${n} to invite · ${left} seat${left === 1 ? "" : "s"} left</div>
<div class="sw-team" role="table" aria-label="Your team">
<div class="sw-team-head" role="row"><span role="columnheader">Name</span><span role="columnheader">Email</span><span role="columnheader">Role</span><span></span></div>
${W.team.map((r, i) => {
    const e = teamBad[i] || {};
    return `<div class="sw-team-row" role="row" data-i="${i}">
<span class="sw-cell ${e.name ? "is-bad" : ""}"><input data-sw-team="name" data-i="${i}" value="${esc(r.name)}" placeholder="Name" aria-label="Name" maxlength="160">${e.name ? `<small class="sw-err">${esc(e.name)}</small>` : ""}</span>
<span class="sw-cell ${e.email ? "is-bad" : ""}"><input data-sw-team="email" data-i="${i}" type="email" value="${esc(r.email)}" placeholder="name@company.com" aria-label="Email" maxlength="200">${e.email ? `<small class="sw-err">${esc(e.email)}</small>` : ""}</span>
<span class="sw-cell ${e.role ? "is-bad" : ""}"><select data-sw-team="role" data-i="${i}" aria-label="Role">${SETUP_ROLES.filter(([k]) => k !== "admin" || S.mainAdmin).map(([k, l]) => `<option value="${k}" ${k === r.role ? "selected" : ""}>${esc(l)}</option>`).join("")}</select></span>
<button type="button" class="icon-btn sw-x" data-action="sw-team-remove" data-i="${i}" aria-label="Remove this row" title="Remove">✕</button></div>`;
  }).join("")}
</div>
<p><button type="button" class="secondary small-btn" data-action="sw-team-add">+ Add another person</button></p>
${bad.team ? `<p class="sw-err" role="alert">${esc(bad.team)}</p>` : ""}`;
}
function step5(): string {
  const s = W.summary, added = s.added || {};
  const lines = [
    s.company ? `Company: ${esc(s.company)}` : "",
    s.industries?.length ? `What you make: ${esc(s.industries.map(industryLabel).join(", "))}` : "",
    ...Object.entries(added).map(([k, v]) => `${esc(k)}: ${esc(v.join(", "))}`),
    s.invited?.length ? `Invited: ${esc(s.invited.map((x) => `${x.name} (${x.role})`).join(", "))}` : "No one invited yet",
  ].filter(Boolean);
  const links = s.invited?.filter((x) => x.inviteLink) || [];
  return `<div class="sw-done"><h4>Falcon is ready.</h4><ul class="sw-summary">${lines.map((l) => `<li>${l}</li>`).join("")}</ul>
${links.length ? `<p class="muted">These invitations couldn't be emailed; copy the link to the person:</p><ul class="sw-summary">${links.map((x) => `<li>${esc(x.name)}: <code>${esc(x.inviteLink!)}</code></li>`).join("")}</ul>` : ""}
<h4>Suggested first steps</h4>
<div class="sw-next">
<a href="/app/database/suppliers" data-action="sw-go" data-href="/app/database/suppliers">Add suppliers and agents <small>Partners</small></a>
<a href="/app/fabrics" data-action="sw-go" data-href="/app/fabrics">Add fabric and trims <small>Products</small></a>
<a href="/app/items/new/product" data-action="sw-go" data-href="/app/items/new/product">Create your first product <small>Products</small></a>
<a href="/app/po/new" data-action="sw-go" data-href="/app/po/new">Create your first PO <small>Purchase Orders</small></a>
</div></div>`;
}

function draw(): void {
  const body = [step1, step2, step3, step4, step5][W.step - 1]();
  const last = W.step === 5;
  setContent(`<div class="sw" id="sw" data-step="${W.step}">
${stepBar()}
<section class="panel sw-panel"><h3 class="sw-title">${W.step}. ${esc(SETUP_STEPS[W.step - 1])}</h3>
<div class="form-message" role="alert" hidden></div>
${body}</section>
<div class="sw-bar">
${W.step > 1 && !last ? '<button type="button" class="secondary" data-action="sw-back">Back</button>' : "<span></span>"}
<div class="sw-bar-right">
${last ? "" : '<button type="button" class="secondary" data-action="sw-later">Finish later</button>'}
${W.step === 4 ? '<button type="button" class="secondary" data-action="sw-skip">Skip for now</button><button type="button" class="primary" data-action="sw-send">Send invitations</button>' : last ? '<button type="button" class="primary" data-action="sw-go" data-href="/app">Go to Home</button>' : '<button type="button" class="primary" data-action="sw-next">Next</button>'}
</div></div></div>`);
  const first = document.querySelector<HTMLElement>(".sw-box.is-bad input, .sw-box.is-bad select, .sw-box.is-bad button, .sw-cell.is-bad input");
  first?.focus();
}
function message(text: string): void {
  const m = document.querySelector<HTMLElement>("#sw .form-message");
  if (m) { m.hidden = !text; m.textContent = text; }
}

// ---- Keeping what was typed --------------------------------------------------------------------------------------------
document.addEventListener("input", (e) => {
  const t = e.target as HTMLInputElement;
  if (!t.closest?.("#sw")) return;
  if (t.id === "sw-name") W.company.name = t.value;
  if (t.id === "sw-country") {
    const c = COUNTRIES.find((x) => x.name.toLowerCase() === t.value.trim().toLowerCase());
    if (c && c.code !== W.company.countryCode) {
      // Choosing a country fills in its time zone and local currency (both stay editable).
      W.company.countryCode = c.code;
      W.company.timezone = c.tz;
      W.company.currency = c.currency;
      const tz = document.getElementById("sw-tz") as HTMLSelectElement | null, cur = document.getElementById("sw-cur") as HTMLSelectElement | null;
      if (tz) { if (![...tz.options].some((o) => o.value === c.tz)) tz.add(new Option(c.tz, c.tz)); tz.value = c.tz; }
      if (cur) cur.value = c.currency;
    } else if (!c) W.company.countryCode = "";
  }
  if (t.dataset.swTeam) {
    const i = Number(t.dataset.i);
    (W.team[i] as any)[t.dataset.swTeam] = t.value;
    const s = document.getElementById("sw-seats");
    if (s) { const n = teamRows().length, left = seatsLeft(); s.textContent = `${n} to invite · ${left} seat${left === 1 ? "" : "s"} left`; s.classList.toggle("is-over", n > left); }
  }
});
document.addEventListener("change", (e) => {
  const t = e.target as HTMLInputElement;
  if (!t.closest?.("#sw")) return;
  if (t.id === "sw-tz") W.company.timezone = t.value;
  if (t.id === "sw-cur") W.company.currency = t.value;
  if (t.id === "sw-trade") W.company.tradingCurrency = t.value;
  if (t.dataset.swTick) {
    const key = t.dataset.swTick as StarterListKey;
    const cur = new Set((W.lists[key] as string[]) || []);
    if (t.checked) cur.add(t.value); else cur.delete(t.value);
    W.lists[key] = [...cur];
  }
  if (t.dataset.swQc !== undefined) W.lists.qc = t.checked;
  if (t.dataset.swTeam === "role") W.team[Number(t.dataset.i)].role = t.value;
});
onChange("sw-logo", async (el) => {
  const input = el as HTMLInputElement;
  const file = input.files?.[0];
  input.value = "";
  if (!file) return;
  try {
    await put("/api/org/logo", { dataUrl: await prepareLogo(file) });
    await refreshSession();
    S.company.hasLogo = true;
    toast("Logo uploaded.");
    draw();
  } catch (err) { toast((err as Error).message, "error"); }
});
onAction("sw-industry", (el) => {
  const k = el.dataset.k!;
  W.industries = W.industries.includes(k) ? W.industries.filter((x) => x !== k) : [...W.industries, k];
  el.classList.toggle("is-on", W.industries.includes(k));
  el.setAttribute("aria-pressed", String(W.industries.includes(k)));
  el.querySelector(".sw-tick")!.textContent = W.industries.includes(k) ? "✓" : "";
  document.getElementById("sw-get")!.innerHTML = getList();
  if (W.industries.length && bad.industries) { delete bad.industries; el.closest(".sw-box")?.classList.remove("is-bad"); el.closest(".sw-box")?.querySelector(".sw-err")?.remove(); }
});
onAction("sw-team-add", () => { W.team.push({ name: "", email: "", role: "merchandiser" }); draw(); document.querySelector<HTMLInputElement>(`[data-sw-team="name"][data-i="${W.team.length - 1}"]`)?.focus(); });
onAction("sw-team-remove", (el) => { W.team.splice(Number(el.dataset.i), 1); teamBad.splice(Number(el.dataset.i), 1); if (!W.team.length) W.team.push({ name: "", email: "", role: "merchandiser" }); draw(); });

// ---- Moving between steps ----------------------------------------------------------------------------------------------
const saveProgress = (step: number) => put("/api/org/setup/progress", { step, data: { company: W.company, industries: W.industries, lists: W.lists, team: W.team, summary: W.summary } });
async function go(step: number) { W.step = step; bad = {}; await saveProgress(step).catch(() => {}); draw(); window.scrollTo(0, 0); }

async function next(): Promise<void> {
  if (busy) return;
  bad = {};
  if (W.step === 1) {
    if (!W.company.name.trim()) bad.name = "Enter the company name.";
    if (!W.company.countryCode) bad.country = "Choose your country from the list.";
    if (Object.keys(bad).length) return draw();
    await apply(() => put("/api/org/setup/company", W.company), () => { W.summary.company = W.company.name.trim(); });
  } else if (W.step === 2) {
    if (!W.industries.length) { bad.industries = "Pick at least one."; return draw(); }
    await apply(() => put("/api/org/setup/industries", { industries: W.industries }), () => { W.summary.industries = [...W.industries]; });
  } else if (W.step === 3) {
    const show = starterListsFor(W.industries);
    const pick = (k: StarterListKey) => (show.includes(k) ? (W.lists[k] as string[]) : []);
    await apply(() => post<any>("/api/org/setup/lists", { garmentSizes: pick("garmentSizes"), shoeSizes: pick("shoeSizes"), standards: pick("standards"), seasons: pick("seasons"), colours: pick("colours"), qc: !!W.lists.qc }),
      (r) => { W.summary.added = { ...(W.summary.added || {}), ...(r?.added || {}) }; });
  }
}
async function apply(call: () => Promise<any>, after: (r: any) => void): Promise<void> {
  busy = true;
  try {
    const r = await call();
    after(r);
    await refreshSession();
    await go(W.step + 1);
  } catch (err) {
    message((err as Error).message);
  } finally { busy = false; }
}
onAction("sw-next", () => { void next(); });
onAction("sw-back", () => { void go(Math.max(1, W.step - 1)); });
onAction("sw-later", async () => {
  try { await saveProgress(W.step); await refreshSession(); } catch { /* kept on the next visit anyway */ }
  toast("Saved. Continue any time from the banner on Home or Settings → Organisation.");
  void navigate("/app");
});
onAction("sw-skip", () => { teamBad = []; void go(5); });
onAction("sw-send", async () => {
  if (busy) return;
  const rows = teamRows();
  teamBad = teamProblems(W.team.map((r) => (r.name.trim() || r.email.trim() ? r : { ...r, name: "x", email: "x@x.x" })), S.emails) as Array<Record<string, string>>;
  // Empty rows are ignored (not red); duplicates and bad emails turn red.
  W.team.forEach((r, i) => { if (!r.name.trim() && !r.email.trim()) teamBad[i] = {}; });
  bad = {};
  if (!rows.length) { bad.team = "Add someone, or press Skip for now."; return draw(); }
  if (rows.length > seatsLeft()) { bad.team = `You have ${seatsLeft()} seat${seatsLeft() === 1 ? "" : "s"} left. Remove ${rows.length - seatsLeft()} ${rows.length - seatsLeft() === 1 ? "person" : "people"} or ask Falcon for more seats.`; return draw(); }
  if (teamBad.some((p) => Object.keys(p).length)) return draw();
  busy = true;
  try {
    const r = await post<any>("/api/org/setup/team", { people: rows });
    W.summary.invited = [...(W.summary.invited || []), ...r.results];
    S.emails = [...S.emails, ...rows.map((x) => x.email.trim().toLowerCase())];
    W.team = [{ name: "", email: "", role: "merchandiser" }];
    teamBad = [];
    await go(5);
  } catch (err) {
    if (err instanceof ApiError && err.details?.rows) { teamBad = err.details.rows; draw(); }
    else message((err as Error).message);
  } finally { busy = false; }
});
// Done: Go to Home or a suggested first step finishes the set-up.
onAction("sw-go", async (el, ev) => {
  ev.preventDefault();
  try { await post("/api/org/setup/complete", {}); await refreshSession(); } catch (err) { return toast((err as Error).message, "error"); }
  void navigate(el.dataset.href || "/app");
});

// ---- Home banner (the main Admin only, until the set-up is finished) -----------------------------------------------------
export function setupBannerHtml(): string {
  const o = state.session.organisation;
  if (!o || o.setupDone !== false || !o.mainAdmin) return "";
  return `<div class="sw-banner" role="status"><span>Finish setting up Falcon</span><a href="/app/setup" data-link>Continue</a></div>`;
}

// ---- Settings → Organisation: Set-up wizard and What you make --------------------------------------------------------------
export function whatYouMakeHtml(): string {
  const o = state.session.organisation;
  const ind = o?.industries || [];
  return `<div class="panel sw-settings" id="sw-settings"><div class="panel-head"><div><h3>What you make</h3><span class="muted">Switches the matching features on. Anything off is only hidden from menus and forms; no data is deleted.</span></div>
<a class="secondary btn small-btn" href="/app/setup" data-link>Set-up wizard</a></div>
<div class="sw-cards" role="group" aria-label="What you make">${INDUSTRIES.map(([k, label]) => `<button type="button" class="sw-card ${ind.includes(k) ? "is-on" : ""}" data-action="sw-make" data-k="${k}" aria-pressed="${ind.includes(k)}"><span class="sw-tick" aria-hidden="true">${ind.includes(k) ? "✓" : ""}</span><span>${esc(label)}</span></button>`).join("")}</div>
<small class="sw-err" id="sw-make-err" hidden>Choose at least one.</small>
<div class="form-actions"><button type="button" class="primary" data-action="sw-make-save">Save what you make</button></div></div>`;
}
let making: string[] | null = null;
onAction("sw-make", (el) => {
  making = making ?? [...(state.session.organisation?.industries || [])];
  const k = el.dataset.k!;
  making = making.includes(k) ? making.filter((x) => x !== k) : [...making, k];
  el.classList.toggle("is-on", making.includes(k));
  el.setAttribute("aria-pressed", String(making.includes(k)));
  el.querySelector(".sw-tick")!.textContent = making.includes(k) ? "✓" : "";
});
onAction("sw-make-save", async () => {
  const list = making ?? state.session.organisation?.industries ?? [];
  const err = document.getElementById("sw-make-err");
  if (!list.length) { if (err) err.hidden = false; return; }
  if (err) err.hidden = true;
  try {
    await put("/api/org/setup/industries", { industries: list, where: "settings" });
    making = null;
    await refreshSession();
    toast("Saved. Menus and forms now match what you make.");
    void navigate(location.pathname + location.search, true);
  } catch (e) { toast((e as Error).message, "error"); }
});
